import React, { useState } from 'react';

import {
  MeuVoto,
  VotoMotivo,
  VotoValor,
  retirarVotoDoAchado,
  votarNoAchado,
} from '../../api/radarApi';
import { notify } from '../../shared/feedback/notify';

/** O mesmo teto que o DTO do backend impõe. O campo trava aqui para o envio nunca ser recusado. */
export const TAMANHO_MAXIMO_DO_TEXTO_DO_VOTO = 300;

/**
 * Os motivos por voto.
 *
 * ⚠️ A separação é regra do BACKEND (`400` na combinação inversa), não preferência de tela: o
 * motivo entra no prompt da próxima exploração e na administração como leitura do que a equipe
 * pensou — "Já agi com base nisso" num voto negativo inverteria o sentido do registro. Oferecer o
 * chip incompatível produziria um erro que o usuário não teria como entender.
 */
const MOTIVOS: Record<VotoValor, { valor: VotoMotivo; rotulo: string }[]> = {
  UP: [
    { valor: 'UTIL_VOU_VERIFICAR', rotulo: 'Vou verificar' },
    { valor: 'UTIL_JA_AGI', rotulo: 'Já agi com base nisso' },
  ],
  DOWN: [
    { valor: 'JA_SABIA', rotulo: 'Já sabia' },
    { valor: 'NUMERO_NAO_BATE', rotulo: 'O número não bate' },
    { valor: 'IRRELEVANTE', rotulo: 'Irrelevante' },
  ],
};

interface Props {
  radarId: string;
  achadoId: string;
  /** O voto vigente DO REQUERENTE, como o servidor o devolveu. */
  meuVoto: MeuVoto | null;
}

/**
 * O voto num achado do assistente (transversal/at-82, ADR 0046).
 *
 * ## O que ele faz, e por que a contagem não aparece
 *
 * O voto não é "curtida": ele muda o que o Radar procura amanhã. Recorte reprovado pela equipe
 * deixa de ser explorado (e é barrado mesmo se o assistente insistir); recorte aprovado vira
 * acompanhamento diário, re-executado sem gastar modelo. Por isso o texto de apoio explica a
 * consequência — sem ele, o botão parece decorativo.
 *
 * 🔴 **Nenhuma contagem é exibida aqui** — nem da equipe, nem do próprio usuário. Mostrar "12
 * acharam útil" ao lado do botão enviesa o voto seguinte; as contagens vivem só em
 * `/admin/sistema/radar` (decisão de 06/09/2026).
 *
 * ## Falha nunca vira sucesso
 *
 * O estado do botão só muda **depois** de o servidor confirmar, e reflete o `meuVoto` que ele
 * devolveu. Não há atualização otimista: numa falha o botão simplesmente não se move, e o erro
 * aparece pelo `notify`. Isso é mais forte que "reverter em caso de erro" — não existe janela em
 * que a tela afirme um voto que não foi gravado.
 */
const VotoDoAchado: React.FC<Props> = ({ radarId, achadoId, meuVoto }) => {
  const [votoAtual, setVotoAtual] = useState<MeuVoto | null>(meuVoto);
  const [enviando, setEnviando] = useState(false);
  /** Qual voto está com o popover de motivo aberto. `null` = fechado. */
  const [detalhando, setDetalhando] = useState<VotoValor | null>(null);
  const [motivo, setMotivo] = useState<VotoMotivo | null>(null);
  const [texto, setTexto] = useState('');

  const fecharDetalhe = () => {
    setDetalhando(null);
    setMotivo(null);
    setTexto('');
  };

  const clicar = async (voto: VotoValor) => {
    if (enviando) return;
    setEnviando(true);

    try {
      if (votoAtual?.voto === voto) {
        // Clicar no voto ativo desfaz. O backend grava o evento RETIRADO — não apaga o anterior.
        await retirarVotoDoAchado(radarId, achadoId);
        setVotoAtual(null);
        fecharDetalhe();
      } else {
        const { meuVoto: gravado } = await votarNoAchado(radarId, achadoId, { voto });
        setVotoAtual(gravado);
        // O motivo é perguntado DEPOIS de o voto estar gravado: se a pergunta viesse antes, quem
        // fechasse o popover teria clicado no botão sem que nada fosse registrado.
        setDetalhando(voto);
        setMotivo(null);
        setTexto('');
      }
    } catch (error) {
      // Sem falso sucesso: nenhum toast de confirmação, e o botão continua onde estava.
      notify.errorFrom(error, 'Não foi possível registrar o seu voto. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const enviarDetalhe = async () => {
    if (!detalhando || enviando) return;
    setEnviando(true);

    try {
      const limpo = texto.trim();
      const { meuVoto: gravado } = await votarNoAchado(radarId, achadoId, {
        voto: detalhando,
        ...(motivo ? { motivo } : {}),
        ...(limpo ? { texto: limpo } : {}),
      });
      setVotoAtual(gravado);
      fecharDetalhe();
    } catch (error) {
      // O popover fica aberto: o que o usuário escreveu não pode ser descartado por uma falha.
      notify.errorFrom(error, 'Não foi possível registrar o seu voto. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const classeDoBotao = (voto: VotoValor) =>
    votoAtual?.voto === voto ? 'radar-voto-botao radar-voto-botao--ativo' : 'radar-voto-botao';

  return (
    <div className="radar-voto">
      <div className="radar-voto-botoes">
        <button
          type="button"
          className={classeDoBotao('UP')}
          aria-pressed={votoAtual?.voto === 'UP'}
          disabled={enviando}
          onClick={() => clicar('UP')}
        >
          Útil
        </button>
        <button
          type="button"
          className={classeDoBotao('DOWN')}
          aria-pressed={votoAtual?.voto === 'DOWN'}
          disabled={enviando}
          onClick={() => clicar('DOWN')}
        >
          Não útil
        </button>
      </div>

      {detalhando && (
        <div className="radar-voto-motivos">
          <p className="radar-voto-motivos-titulo">
            Seu voto foi registrado. Quer dizer por quê? É opcional.
          </p>

          <div className="radar-voto-chips">
            {MOTIVOS[detalhando].map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                className={
                  motivo === opcao.valor ? 'radar-voto-chip radar-voto-chip--ativo' : 'radar-voto-chip'
                }
                aria-pressed={motivo === opcao.valor}
                onClick={() => setMotivo(motivo === opcao.valor ? null : opcao.valor)}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>

          <textarea
            className="radar-voto-texto"
            // O teto trava no campo, e não só na validação: digitar 400 e ver o envio recusado
            // pelo backend faria o usuário perder o que escreveu.
            maxLength={TAMANHO_MAXIMO_DO_TEXTO_DO_VOTO}
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            placeholder="Quer explicar? Ajuda o assistente a entender."
            aria-label="Comentário sobre este achado"
          />
          <span className="radar-voto-contador">
            {texto.length}/{TAMANHO_MAXIMO_DO_TEXTO_DO_VOTO}
          </span>

          <div className="radar-voto-motivos-acoes">
            <button
              type="button"
              className="do-btn-filtros"
              disabled={enviando}
              onClick={enviarDetalhe}
            >
              Enviar
            </button>
            <button type="button" className="radar-voto-chip" onClick={fecharDetalhe}>
              Agora não
            </button>
          </div>
        </div>
      )}

      <p className="radar-voto-apoio">
        Sua avaliação orienta o assistente: achados reprovados deixam de ser propostos e os
        aprovados passam a ser acompanhados todo dia. A contagem de votos não é exibida aqui.
      </p>
    </div>
  );
};

export default VotoDoAchado;
